import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BarChart3,
  CalendarDays,
  Car,
  CreditCard,
  Download,
  Euro,
  Percent,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../services/api';
import { CHART, chartAxis, chartTooltip, ErrorBox, MeterBar, SkeletonPage } from '../ui/kit';
import './Finance.css';
import PageHeader from '../components/PageHeader';
import { betriebstagSchluessel } from '../lib/zeit';

const RIDE_COLORS = {
  standard: '#141518',
  comfort: '#4b5563',
  premium: '#4b5563',
  xl: '#9c27b0',
  max: '#a15a5a',
};

const RIDE_NAMES = {
  standard: 'VOOGO Basis',
  comfort: 'VOOGO Komfort',
  premium: 'VOOGO Business',
  xl: 'VOOGO Family',
  max: 'VOOGO Van',
  taxi: 'VOOGO Taxi',
};

// Deutsche Labels für den kodierten Storno-Grund (fester Code-Vertrag Kunde/Fahrer/Server).
const CODE_LABELS = {
  plans_changed: 'Meine Pläne haben sich geändert',
  wait_too_long: 'Wartezeit war zu lang',
  wrong_address: 'Falsche Adresse eingegeben',
  driver_not_approaching: 'Fahrer kam nicht näher',
  rider_unreachable: 'Fahrgast nicht erreichbar',
  pickup_route_problem: 'Problem mit der Route zum Abholort',
  unsafe_pickup: 'Kein sicherer Abholort',
  vehicle_problem: 'Problem mit dem Fahrzeug',
  customer_cancelled: 'Vom Kunden storniert',
  no_show: 'Fahrgast nicht erschienen',
  other: 'Anderer Grund',
};

// code kann Alt-Freitext vom Kunden sein — direkter Objekt-Zugriff wäre bei '__proto__' & Co.
// ein Prototype-Lookup (React crasht auf Objekt-Kindern), daher own-property-Check.
const codeLabel = (code) => (
  Object.prototype.hasOwnProperty.call(CODE_LABELS, code) ? CODE_LABELS[code] : code
);

const money = (value) => `${Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

const normalized = (value) => String(value || '').trim().toLocaleLowerCase('de-DE');

// ADMIN-KONTOBEWEGUNG-001 (C4): BERLINER Kalendertag, nicht UTC-Tag oder Gerätezeit.
// `toISOString().split('T')[0]` liefert zwischen 00:00 und 02:00 Berliner Zeit noch GESTERN —
// dann startet die Finanzseite nachts auf dem Vortag und die heutigen Buchungszeilen fehlen.
// `en-CA` ist die Sprachkennung mit JJJJ-MM-TT; `timeZone` bindet die Grenze ausdrücklich
// an das Finanzbuch. Ein Partner auf Reisen darf dadurch keinen anderen Zeitraum erhalten.
const dayString = (date) => betriebstagSchluessel(date);

const shiftDayString = (day, offset) => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date + offset)).toISOString().slice(0, 10);
};

export const defaultFinanceRange = (now = new Date()) => {
  const to = dayString(now);
  return { from: shiftDayString(to, -30), to };
};

function FinanceKpi({ icon: Icon, label, value, detail, tone = 'neutral', featured = false }) {
  return (
    <article className={`finance-kpi is-${tone}${featured ? ' is-featured' : ''}`}>
      <div className="finance-kpi-topline">
        <span className="finance-kpi-icon"><Icon size={15} /></span>
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

export default function Finance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [visiblePartners, setVisiblePartners] = useState(25);
  const initialRange = useMemo(() => defaultFinanceRange(), []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/finance?from=${from}&to=${to}`)
      .then((response) => {
        setData(response.data);
        setLoadError(false);
        setVisiblePartners(25);
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
  }, [from, to]);

  useEffect(() => {
    load();
    // Der initiale Zeitraum wird nur beim Mount geladen; spätere Datumsänderungen
    // bleiben wie bisher bewusst über den Laden-Button gesteuert.
  }, []);

  useEffect(() => {
    setVisiblePartners(25);
  }, [partnerSearch]);

  const summary = data?.summary || {};
  // Backend ist Source of Truth: Buchungszeilen und gespeicherte Periodenauszahlungen
  // werden nur formatiert, niemals im Browser neu berechnet.
  const gmv = parseFloat((summary.total_revenue || 0).toFixed(2));
  const voogoGross = parseFloat((summary.voogo_gross || 0).toFixed(2));
  const partnerPayout = parseFloat((summary.partner_payout || 0).toFixed(2));

  const partners = data?.by_partner || [];
  // P2 (Fußzeile): Die Spalte „Kontobewegung bei VOOGO" hatte in der Gesamtzeile den
  // Auszahlungsbetrag stehen — eine ANDERE Zahl als die Summe der Spalte
  // (partner_payout stammt aus gespeicherten Perioden, ledger_effect aus den
  // Buchungszeilen des Zeitraums). Jetzt steht dort die echte Spaltensumme.
  // Das ist keine Geldformel, sondern die Addition genau der Zeilen, die der Server
  // für diesen Zeitraum geliefert hat (data.by_partner ist vollständig, nie die
  // gefilterte Anzeige) — deshalb bleibt die Gesamtzeile auch bei aktiver Suche wahr.
  const partnerLedgerEffectTotal = parseFloat(
    partners.reduce((sum, partner) => sum + (Number(partner.ledger_effect) || 0), 0).toFixed(2),
  );
  const partnerTerm = normalized(partnerSearch);
  const matchingPartners = useMemo(() => {
    if (!partnerTerm) return partners;
    return partners.filter((partner) => normalized([
      partner.company_name,
      partner.id,
      partner.rides,
    ].join(' ')).includes(partnerTerm));
  }, [partners, partnerTerm]);
  const shownPartners = matchingPartners.slice(0, visiblePartners);

  if (loading && !data) return <SkeletonPage />;

  const exportCSV = () => {
    if (!data?.daily) return;
    const rows = [
      ['Datum', 'Fahrten', 'Umsatz', 'Provision'],
      ...data.daily.map((day) => [
        day.date,
        day.rides,
        day.revenue.toFixed(2),
        day.commission.toFixed(2),
      ]),
    ];
    const csv = rows.map((row) => row.join(',')).join('\n');
    const objectUrl = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `under_finance_${from}_${to}.csv`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  };

  const hasCancelReasons = (data?.cancel_reasons || []).length > 0;

  return (
    <div className="finance-page">
      <PageHeader
        title="Finanzen"
        description="Nachträglich unveränderbare Buchungszeilen und gespeicherte Partnerperioden systemweit abstimmen."
        actions={
        <div className="finance-heading-badges">
          <span><CalendarDays size={12} /> {data?.from || from} – {data?.to || to}</span>
          <span className="is-source"><ShieldCheck size={12} /> Backend-verifiziert</span>
        </div>
      }
      />

      {data && (data.master_data_gaps?.length > 0 || data.invoice_env_missing?.length > 0 || data.open_migration_issues > 0 || data.period_conflicts > 0 || data.settlement_ready !== true) && (
        <section className="finance-mode-banner is-blocker">
          <strong>Finanzblocker offen</strong>
          <ul>
            {(data.master_data_gaps || []).map((gap) => (
              <li key={gap.id}>
                {gap.company_name || gap.id}: fehlende Rechnungsdaten ({gap.missing.join(', ')}) — Fahrten dieses
                Partners lassen sich nicht abschließen.
              </li>
            ))}
            {data.invoice_env_missing?.length > 0 && (
              <li>VOOGO-Rechnungsdaten fehlen auf dem Server: {data.invoice_env_missing.join(', ')}</li>
            )}
            {data.open_migration_issues > 0 && (
              <li>{data.open_migration_issues} offene Eröffnungsstände blockieren die weitere Abrechnung.</li>
            )}
            {data.period_conflicts > 0 && (
              <li>{data.period_conflicts} überlappende historische Partnerperioden müssen explizit geklärt werden.</li>
            )}
            {data.settlement_ready !== true && (
              <li>
                {data.pending_settlements || 0} offene Zahlungsabschlüsse blockieren Berechnung und Freigabe.
                {(data.pending_settlement_partners || []).length > 0 && (
                  <ul className="finance-blocker-detail">
                    {data.pending_settlement_partners.map((partner) => (
                      <li key={partner.id}>
                        <strong>{partner.company_name || partner.id}</strong>
                        {partner.reason ? `: ${partner.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
                {' '}
                <a className="finance-blocker-link" href="/settlements">Zur Werkbank „Zahlungsabschlüsse"</a>
              </li>
            )}
          </ul>
        </section>
      )}

      <section className="finance-controls" aria-label="Finanzzeitraum">
        <div className="finance-controls-copy">
          <span className="finance-section-icon"><CalendarDays size={15} /></span>
          <div>
            <span className="finance-overline">Berichtszeitraum</span>
            <h2>Finanzperiode laden</h2>
            <small>Jedes Datum meint den Betriebstag 04:00–03:59 in Europe/Berlin.</small>
          </div>
        </div>
        <div className="finance-date-fields">
          <label>
            <span>Von</span>
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <span className="finance-date-separator">bis</span>
          <label>
            <span>Bis</span>
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
        </div>
        <div className="finance-control-actions">
          <button className="is-primary" type="button" onClick={load} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'is-spinning' : ''} />
            {loading ? 'Aktualisiert…' : 'Zeitraum laden'}
          </button>
          <button className="is-secondary" type="button" onClick={exportCSV} disabled={!data?.daily}>
            <Download size={13} /> CSV exportieren
          </button>
        </div>
      </section>

      <section className="finance-principle">
        <span><ShieldCheck size={16} /></span>
        <div>
          <strong>Buchungszeilen und gespeicherte Perioden sind die finanziellen Datenquellen</strong>
          <p>Ride-Zustände werden hier weder zu Auszahlungen noch zu Abrechnungssalden neu berechnet.</p>
        </div>
        <i>Alle Beträge brutto</i>
      </section>

      {loadError && (
        <ErrorBox
          text={data ? 'Die Finanzperiode konnte nicht aktualisiert werden.' : 'Finanzdaten konnten nicht geladen werden.'}
          onRetry={load}
          style={{ marginBottom: 12 }}
        />
      )}

      <section className="finance-kpis" aria-label="Finanzkennzahlen">
        <FinanceKpi
          icon={TrendingUp}
          label="Vermitteltes Fahrgeld"
          value={money(gmv)}
          detail={`${summary.total_rides || 0} Fahrten`}
          tone="brand"
          featured
        />
        <FinanceKpi
          icon={Percent}
          label="VOOGO-Umsatz · Provision brutto"
          value={money(voogoGross)}
          detail={`Vermittlung ${money(summary.voogo_commission)} + Service ${money(summary.voogo_service)}`}
          tone="ink"
          featured
        />
        <FinanceKpi
          icon={Euro}
          label="Auszahlungsbetrag an Partner"
          value={money(partnerPayout)}
          detail="Summe der gespeicherten Abrechnungsperioden"
          tone="neutral"
          featured
        />
        <FinanceKpi
          icon={CreditCard}
          label="Kartenzahlungen"
          value={money(summary.card_revenue)}
          detail={`Barzahlungen: ${money(summary.cash_revenue)}`}
        />
        <FinanceKpi
          icon={AlertCircle}
          label="Wartegebühren"
          value={money(summary.total_wait_fees)}
          detail={`Störungsgebühren: ${money(summary.total_cancellation_fees)}`}
          tone="warning"
        />
        <FinanceKpi
          icon={AlertCircle}
          label="Stornierungen"
          value={data?.cancelled_total || 0}
          detail={`No-Shows: ${summary.no_show_count || 0}`}
          tone="danger"
        />
        {/* STORNOGEBUEHR-ANKOMMEN-001 (Owner: „eine Zahl"): was Kunden VOOGO an Stornogebühren noch schulden —
            zeitraumunabhängig, denn der Partner wurde dafür bereits ausgezahlt. */}
        <FinanceKpi
          icon={AlertCircle}
          label={summary.open_fare_shortfall_count > 0 ? 'Offene Kundenbeträge gesamt' : 'Offene Stornogebühren gesamt'}
          value={money(summary.open_cancellation_fees_total || 0)}
          detail={`${summary.open_cancellation_fees_count || 0} offen bei ${summary.open_cancellation_fees_customers || 0} Kunden${summary.open_fare_shortfall_count > 0 ? ` · davon ${summary.open_fare_shortfall_count} Fahrpreisreste` : ''} · unabhängig vom Zeitraum`}
          tone="warning"
        />
      </section>

      <section className="finance-flow-grid">
        <article className="finance-panel finance-cashflow">
          <header className="finance-panel-heading">
            <span className="finance-section-icon"><Wallet size={15} /></span>
            <div>
              <span className="finance-overline">Abstimmung</span>
              <h2>Geldfluss</h2>
            </div>
            <b>Brutto</b>
          </header>
          <div className="finance-ledger">
            {[
              { label: 'Vermitteltes Fahrgeld', hint: 'Fahrpreise, Wartezeit und Stornopositionen', value: gmv },
              { label: 'VOOGO-Gebühren', hint: 'Take + Service, brutto', value: voogoGross },
              { label: 'Trinkgeld', hint: 'gespeicherte Information', value: summary.total_tips || 0 },
              { label: 'Auszahlungsbetrag', hint: 'aus gespeicherten Perioden', value: partnerPayout, total: true },
            ].map((item) => (
              <div className={item.total ? 'is-total' : ''} key={item.label}>
                <span><strong>{item.label}</strong><small>{item.hint}</small></span>
                <b>{money(item.value)}</b>
              </div>
            ))}
          </div>
          <p className="finance-accounting-note">
            Diese Analyse ist keine Reconciliation. Verbindlich sind die getrennten, unveränderlichen Periodendokumente unter Abrechnung.
          </p>
        </article>

        <article className="finance-panel finance-distribution">
          <header className="finance-panel-heading">
            <span className="finance-section-icon"><BarChart3 size={15} /></span>
            <div>
              <span className="finance-overline">Verteilung</span>
              <h2>Zahlungsarten und Fahrttypen</h2>
            </div>
          </header>

          <div className="finance-payment-grid">
            {[
              { label: 'Karte', value: summary.card_revenue || 0, color: '#9c27b0' },
              { label: 'Guthaben', value: summary.wallet_revenue || 0, color: '#4b5563' },
              { label: 'Bar', value: summary.cash_revenue || 0, color: '#4b5563' },
            ].map((item) => {
              const total = summary.total_revenue || 1;
              return (
                <div className="finance-payment" key={item.label}>
                  <span><strong>{item.label}</strong><small>{Math.round(item.value / total * 100)} %</small></span>
                  <b>{money(item.value)}</b>
                  <MeterBar value={item.value} max={total} color={item.color} height={6} />
                </div>
              );
            })}
          </div>

          <div className="finance-ride-types">
            {['standard', 'comfort', 'premium', 'xl', 'max'].map((type) => {
              const value = summary[`${type}_revenue`] || 0;
              if (!value) return null;
              return (
                <div key={type}>
                  <span>{RIDE_NAMES[type]}</span>
                  <MeterBar value={value} max={summary.total_revenue || 1} color={RIDE_COLORS[type]} height={5} />
                  <b style={{ color: RIDE_COLORS[type] }}>{money(value)}</b>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className={`finance-analysis-grid${hasCancelReasons ? '' : ' without-cancellations'}`}>
        <article className="finance-panel finance-chart">
          <header className="finance-panel-heading">
            <span className="finance-section-icon"><TrendingUp size={15} /></span>
            <div>
              <span className="finance-overline">Zeitverlauf</span>
              <h2>Tagesumsatz</h2>
            </div>
            <div className="finance-chart-legend">
              <span><i className="is-gmv" /> Fahrgeld</span>
              <span><i className="is-commission" /> VOOGO-Provision</span>
            </div>
          </header>
          <div className="finance-chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.daily || []} margin={{ left: 0, right: 10 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="date" {...chartAxis} tickFormatter={(date) => date?.slice(5)} />
                <YAxis {...chartAxis} tickFormatter={(value) => `${value}€`} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={chartTooltip}
                  formatter={(value, name) => [
                    `${value.toFixed(2)} €`,
                    name === 'revenue' ? 'Fahrgeld' : 'VOOGO-Provision',
                  ]}
                />
                <Bar dataKey="revenue" fill={CHART.ink} radius={CHART.barRadius} name="revenue" />
                <Bar dataKey="commission" fill="#9c27b0" radius={CHART.barRadius} name="commission" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        {hasCancelReasons && (
          <article className="finance-panel finance-cancellations">
            <header className="finance-panel-heading">
              <span className="finance-section-icon is-danger"><AlertCircle size={15} /></span>
              <div>
                <span className="finance-overline">Qualitätsindikator</span>
                <h2>Stornogründe</h2>
              </div>
              <b>{data.cancelled_total || 0}</b>
            </header>
            <div className="finance-cancel-list">
              {data.cancel_reasons.map(({ code, n }) => (
                <div key={code}>
                  <span>{codeLabel(code)}</span>
                  <b>{n}</b>
                </div>
              ))}
            </div>
          </article>
        )}
      </section>

      {partners.length > 0 && (
        <section className="finance-partners">
          <header className="finance-partners-heading">
            <div>
              <span className="finance-overline">Systemweite Aufschlüsselung</span>
              <h2>Umsatz nach Partner</h2>
              <p>Bereits geladene Partner lokal durchsuchen und vergleichen.</p>
            </div>
            <div className="finance-partner-tools">
              <label className="finance-search">
                <Search size={14} />
                <span className="sr-only">Partner lokal durchsuchen</span>
                <input
                  type="search"
                  value={partnerSearch}
                  onChange={(event) => setPartnerSearch(event.target.value)}
                  placeholder="Partner oder ID suchen…"
                />
              </label>
              <span className="finance-visible-count">
                <b>{Math.min(shownPartners.length, matchingPartners.length)}</b> von {matchingPartners.length} sichtbar
              </span>
            </div>
          </header>

          <div className="finance-table-wrap">
            <table>
              <thead>
                <tr>
                  {['Partner', 'Fahrten', 'Fahrgeld (brutto)', 'VOOGO-Gebühren'].map((heading) => (
                    <th key={heading}>{heading}</th>
                  ))}
                  {/* Zweizeilig umbrochen statt gekürzt: ohne „bei VOOGO" liest sich die Zahl
                      als Bewegung auf einem Bankkonto. Der Textinhalt bleibt einzeilig lesbar. */}
                  <th className="finance-th-stacked"><span>Kontobewegung</span>{' '}<span>bei VOOGO</span></th>
                </tr>
              </thead>
              <tbody>
                {shownPartners.length === 0 ? (
                  <tr className="finance-empty-row">
                    <td colSpan="5">Kein bereits geladener Partner passt zur Suche.</td>
                  </tr>
                ) : shownPartners.map((partner) => (
                  <tr key={partner.id}>
                    <td>
                      <span className="finance-partner-name">
                        <i>{String(partner.company_name || 'P').slice(0, 2).toUpperCase()}</i>
                        <span><strong>{partner.company_name}</strong><small>Partner-ID {partner.id}</small></span>
                      </span>
                    </td>
                    <td>{partner.rides}</td>
                    <td><strong>{money(partner.revenue)}</strong></td>
                    <td>{money(partner.commission)}</td>
                    <td className="is-payout">{money(partner.ledger_effect)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Gesamt</td>
                  <td>{summary.total_rides}</td>
                  <td>{money(gmv)}</td>
                  <td>{money(voogoGross)}</td>
                  <td className="is-payout">{money(partnerLedgerEffectTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="finance-accounting-note">
            Kontobewegung bei VOOGO: Information – keine Rechnung und keine Auszahlung.
            Plus heißt: VOOGO schuldet dem Partner. Minus heißt: der Partner schuldet VOOGO.
            Bargeld ist nicht enthalten — das liegt bereits beim Fahrer.
            Die Zeile „Gesamt“ ist die Summe dieser Spalte über den ganzen Zeitraum, auch wenn
            die Suche gerade Zeilen ausblendet. Der Auszahlungsbetrag ist eine andere Zahl und
            steht oben in „Auszahlungsbetrag an Partner“ und im Geldfluss.
          </p>

          {matchingPartners.length > visiblePartners && (
            <div className="finance-load-more">
              <span>Noch {matchingPartners.length - visiblePartners} Partner in der geladenen Periode</span>
              <button type="button" onClick={() => setVisiblePartners((current) => current + 25)}>
                Weitere 25 anzeigen
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
